// The rules the trigger editor keeps to: what the backend's triggers.validate() takes, and the text
// rule views.py applies to an agent's title, instruction and a watch's question, as plain functions.
// The editor and the plan card use them to say what is missing and to hold Deploy back.
//
// triggerRules.cases.json is the table that keeps this file and the backend together. It holds what
// the real validate() says for a few thousand triggers, and the numbers it works to. The backend
// test (backend/api/tests/test_agents_trigger_rules.py) holds the table to the backend;
// triggerRules.test.js holds draftHint() and LIMITS to the table. Change validate(), the table
// (regenerate it with `cd backend && python api/tests/make_trigger_rules_cases.py`) and this file
// together.
//
// Two things are deliberately not the backend's: the clock (a time that has passed, or is more than
// two years off, is the server's call) and anything validate() takes that the card has no words for
// (a watch with no interval or days). Those wait for the server, or are held back; the table marks
// them (its "clock" and "unreadable" groups).
import { EVENTS, UNREADABLE, describeTrigger } from './triggerText';

export const KINDS = ['now', 'once', 'repeat', 'event', 'watch'];
// C is confirmed, R asked to reschedule, U asked and no answer yet. Not "unclear".
export const STATUS_CODES = ['C', 'R', 'U'];

// The numbers validate() works to, with the table's names for them. The table's "limits" block is
// the backend's own answer; a test holds this object equal to it.
export const LIMITS = {
  hours: [1, 24],
  visit_minutes: [5, 240],
  visit_default_minutes: 30,
  watch_every_minutes: [15, 1440],
  watch_default_every_minutes: 120,
  watch_days: [1, 90],
  watch_default_days: 14,
  max_check_length: 500,
  max_list_entries: 64,
};

export const PICK_WHEN = 'Pick when it runs';
// validate()'s last word on a watch (see draftHint). The backend's own sentence.
const NEVER_CHECKS = 'This watch would never check. Pick a shorter interval or more days.';
// The three dots of the backend's "Edit out the [[...]] marker" sentence, as one character.
const ELLIPSIS = String.fromCharCode(0x2026);
// A number moved into [low, high], as validate() moves a watch's days.
const within = (number, [low, high]) => Math.min(high, Math.max(low, number));

export const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
export const text = (value) => (typeof value === 'string' ? value : '');
// _whole(): a number from JSON that is a whole number. (A boolean is not one.)
export const whole = (value) => typeof value === 'number' && Number.isInteger(value);

// ---- reading text the way Python does ---------------------------------------------------------

// The blanks Python's str.strip() and str.split() work with: the code points str.isspace() is true
// for. The same table as PY_SPACE in triggerText.js, which keeps its own copy private. JS trim() and
// \s differ: they add U+FEFF and miss 0x1c to 0x1f and 0x85. The table's rows hold every blank.
const PY_SPACE = [
  [0x09, 0x0d], [0x1c, 0x20], [0x85, 0x85], [0xa0, 0xa0], [0x1680, 0x1680], [0x2000, 0x200a],
  [0x2028, 0x2029], [0x202f, 0x202f], [0x205f, 0x205f], [0x3000, 0x3000],
];
const isPySpace = (unit) => PY_SPACE.some(([low, high]) => unit >= low && unit <= high);

// str.strip()
export function pyStrip(value) {
  let start = 0;
  let end = value.length;
  while (start < end && isPySpace(value.charCodeAt(start))) start += 1;
  while (end > start && isPySpace(value.charCodeAt(end - 1))) end -= 1;
  return value.slice(start, end);
}

// " ".join(value.split())
export function collapse(value) {
  const words = [];
  let word = '';
  for (let i = 0; i < value.length; i += 1) {
    if (!isPySpace(value.charCodeAt(i))) {
      word += value[i];
    } else if (word) {
      words.push(word);
      word = '';
    }
  }
  if (word) words.push(word);
  return words.join(' ');
}

// triggers.UNSTORABLE: a NUL, or half of a surrogate pair (Postgres can't keep either).
function unstorable(value) {
  for (let i = 0; i < value.length; i += 1) {
    const unit = value.charCodeAt(i);
    if (unit === 0) return true;
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(i + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      i += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return true;
    }
  }
  return false;
}

// Why the backend would refuse this text as an agent's `what` (views._text_problem), or null.
export function textProblem(value, what) {
  const body = text(value);
  if (unstorable(body)) return `The ${what} has a character that can't be saved.`;
  if (body.includes('[[') || body.includes(']]')) return `Edit out the [[${ELLIPSIS}]] marker in the ${what}.`;
  return null;
}

// "HH:MM" or "H:MM" on the 24-hour clock (triggers.hm), and "YYYY-MM-DD" naming a real day
// (triggers._day), each with Python's blanks taken off the ends first. Spelled out so the editor
// and the backend agree on the odd ones.
const TIME = /^([0-9]{1,2}):([0-9]{2})$/;
const DAY = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const isLeapYear = (year) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

function validTime(value) {
  const match = typeof value === 'string' ? TIME.exec(pyStrip(value)) : null;
  return match !== null && Number(match[1]) <= 23 && Number(match[2]) <= 59;
}

function validDay(value) {
  const match = typeof value === 'string' ? DAY.exec(pyStrip(value)) : null;
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1 || month < 1 || month > 12) return false;
  return day >= 1 && day <= (month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]);
}

// A status entry as the backend reads it: Python's strip(), then upper().
export const statusOf = (entry) => (typeof entry === 'string' ? pyStrip(entry).toUpperCase() : '');

// ---- what is missing --------------------------------------------------------------------------

// A few words on what stops this trigger being deployed, or null when the backend would take it.
// It follows triggers.validate() rule for rule except the clock, and adds one rule of its own: a
// trigger the card has no words for (describeTrigger says "Schedule can't be read") is not ready.
export function draftHint(trigger) {
  // null and {} are "now" (triggers.py: the first rule of validate); any other thing that isn't an
  // object is refused.
  if (trigger === null || trigger === undefined) return null;
  if (!isObject(trigger)) return PICK_WHEN;
  if (Object.keys(trigger).length === 0) return null;
  const t = trigger;
  if (!KINDS.includes(t.kind)) return PICK_WHEN;
  if (t.kind === 'now') return null;
  if (t.kind === 'once') {
    if (!validDay(t.date)) return 'Pick a date';
    if (!validTime(t.time)) return 'Pick a time';
  } else if (t.kind === 'repeat') {
    if (t.every_hours != null) {
      const [low, high] = LIMITS.hours;
      if (!whole(t.every_hours) || t.every_hours < low || t.every_hours > high) return `Pick ${low} to ${high} hours`;
    } else {
      if (!validTime(t.time)) return 'Pick a time';
      const days = t.days == null ? [] : t.days;
      if (!Array.isArray(days) || days.length > LIMITS.max_list_entries || !days.every((d) => whole(d) && d >= 0 && d <= 6)) {
        return 'Pick at least one day';
      }
    }
  } else if (t.kind === 'event') {
    if (!EVENTS.some(([key]) => key === t.event)) return 'Pick an event';
    if (t.event === 'visit_soon' && t.minutes != null && (!whole(t.minutes) || t.minutes > LIMITS.visit_minutes[1])) {
      return `Pick ${LIMITS.visit_minutes[0]} to ${LIMITS.visit_minutes[1]} minutes before the visit`;
    }
    if (t.event === 'confirmation' && t.statuses != null) {
      const bad = !Array.isArray(t.statuses) || t.statuses.length > LIMITS.max_list_entries
        || !t.statuses.every((s) => STATUS_CODES.includes(statusOf(s)));
      if (bad) return 'Pick at least one reply';
    }
    if (t.event === 'new_report' && t.this_patient != null && typeof t.this_patient !== 'boolean') {
      return 'this_patient must be true or false.';
    }
  } else {
    const check = collapse(text(t.check));
    if (!check) return 'Say what to watch for';
    if (Array.from(check).length > LIMITS.max_check_length) return `Keep the check to ${LIMITS.max_check_length} characters or fewer.`;
    const problem = textProblem(check, "watch's check");
    if (problem) return problem;
    if (t.every_minutes != null && (!whole(t.every_minutes) || t.every_minutes > LIMITS.watch_every_minutes[1])) {
      return `Pick ${LIMITS.watch_every_minutes[0]} to ${LIMITS.watch_every_minutes[1]} minutes between checks`;
    }
    if (t.days != null && !whole(t.days)) return `Pick ${LIMITS.watch_days[0]} to ${LIMITS.watch_days[1]} days`;
    if (t.repeat != null && typeof t.repeat !== 'boolean') return 'repeat must be true or false.';
    // The last thing validate() looks at. A repeating watch doesn't check the moment it is set (see
    // watchChecks), so one whose interval is as long as the watch itself has no check to make. The
    // numbers are the ones validate() works with: what is left out is the default, the interval is
    // raised to its floor and the days are moved into their range. (A one-shot watch checks as
    // soon as it is set, and only a real true is a repeating one.)
    const every = Math.max(t.every_minutes ?? LIMITS.watch_default_every_minutes, LIMITS.watch_every_minutes[0]);
    const days = within(t.days ?? LIMITS.watch_default_days, LIMITS.watch_days);
    if (t.repeat === true && every >= days * 24 * 60) return NEVER_CHECKS;
  }
  // The card's own rule: whatever describeTrigger can't put in words is not ready either.
  return describeTrigger(t) === UNREADABLE ? 'Finish the schedule' : null;
}

// ---- how many checks ----------------------------------------------------------------------------

// triggers.watch_checks(): how many checks a watch makes before it ends, the number "About N checks"
// shows on the plan card; 0 for a trigger it can't read. The minutes of its days over its interval,
// rounded down. A watch that fires once checks as soon as it is set; a repeating one (repeat is
// true) does not, because that first check would look back over nothing, so it makes one fewer. The
// estimate rounds down, so it is 0 for some watches the backend takes (a repeating one that checks
// once, before it ends); the card shows no estimate then. The table's "checks" rows hold the
// backend's answers for a sweep of watches of both sorts, and a test holds this to them.
export function watchChecks(trigger) {
  if (!isObject(trigger)) return 0;
  const { days, every_minutes: every } = trigger;
  if (!whole(days) || !whole(every) || days < 0 || every < 1) return 0;
  const checks = Math.floor((days * 24 * 60) / every);
  return trigger.repeat === true ? Math.max(checks - 1, 0) : checks;
}

// ---- comparing triggers -----------------------------------------------------------------------

// A value as text with sorted keys, so two triggers compare by what they hold, as the server
// compares them (key order doesn't matter), and not by the order their keys were written in.
export function canonical(value) {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isObject(value)) {
    const keys = Object.keys(value).filter((key) => value[key] !== undefined).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}
